import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/AppSidebar'
import { AppHeader } from '@/components/AppHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { useUserProfile } from '@/hooks/useUserProfile'
import { useToast } from '@/hooks/use-toast'
import { Trophy, Ticket, Star, Shield, Zap, Crown, Calendar, Package, Plus, ShoppingBag, Clock, CheckCircle, AlertCircle } from 'lucide-react'

interface RewardItem {
  id: string
  title: string
  description: string
  image: string
  cost: number
  currency: 'points' | 'passes'
  category: 'digital' | 'seasonal' | 'irl'
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  featured?: boolean
}

interface InventoryItem {
  id: string
  reward_id: string
  title: string
  status: 'active' | 'pending' | 'used'
  redeemed_at: string
}

const SAMPLE_REWARDS: RewardItem[] = [
  {
    id: '1',
    title: 'Golden Avatar Frame',
    description: 'Stand out with this exclusive golden border around your avatar',
    image: '🏆',
    cost: 500,
    currency: 'points',
    category: 'digital',
    rarity: 'rare',
    featured: true
  },
  {
    id: '2',
    title: 'XP Boost (2x)',
    description: 'Double your XP earnings for the next 7 days',
    image: '⚡',
    cost: 250,
    currency: 'points',
    category: 'digital',
    rarity: 'common'
  },
  {
    id: '3',
    title: 'Champion Badge',
    description: 'Limited-time seasonal badge for winter champions',
    image: '❄️',
    cost: 1,
    currency: 'passes',
    category: 'seasonal',
    rarity: 'epic',
    featured: true
  },
  {
    id: '4',
    title: 'VIP League Entry',
    description: 'Skip the entry fee for any premium league tournament',
    image: '👑',
    cost: 2,
    currency: 'passes',
    category: 'digital',
    rarity: 'legendary'
  },
  {
    id: '5',
    title: 'Custom Team Logo',
    description: 'Upload your own team logo for all your league entries',
    image: '🎨',
    cost: 750,
    currency: 'points',
    category: 'digital',
    rarity: 'rare'
  },
  {
    id: '6',
    title: 'Early Draft Access',
    description: 'Get priority access to draft rooms 5 minutes early',
    image: '🚀',
    cost: 300,
    currency: 'points',
    category: 'digital',
    rarity: 'common'
  }
]

const SAMPLE_INVENTORY: InventoryItem[] = [
  {
    id: '1',
    reward_id: '2',
    title: 'XP Boost (2x)',
    status: 'active',
    redeemed_at: '2024-01-10T10:00:00Z'
  },
  {
    id: '2',
    reward_id: '5',
    title: 'Custom Team Logo',
    status: 'pending',
    redeemed_at: '2024-01-09T15:30:00Z'
  },
  {
    id: '3',
    reward_id: '1',
    title: 'Golden Avatar Frame',
    status: 'used',
    redeemed_at: '2024-01-05T09:15:00Z'
  }
]

const getRarityColor = (rarity: RewardItem['rarity']) => {
  switch (rarity) {
    case 'common': return 'bg-muted text-muted-foreground'
    case 'rare': return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    case 'epic': return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    case 'legendary': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    default: return 'bg-muted text-muted-foreground'
  }
}

const getStatusIcon = (status: InventoryItem['status']) => {
  switch (status) {
    case 'active': return <CheckCircle className="h-4 w-4 text-success" />
    case 'pending': return <Clock className="h-4 w-4 text-warning" />
    case 'used': return <AlertCircle className="h-4 w-4 text-muted-foreground" />
  }
}

const Rewards = () => {
  const navigate = useNavigate()
  const { userProfile } = useUserProfile()
  const { toast } = useToast()
  
  const [selectedReward, setSelectedReward] = useState<RewardItem | null>(null)
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false)
  const [inventory] = useState<InventoryItem[]>(SAMPLE_INVENTORY)

  const handleRedeemClick = (reward: RewardItem) => {
    setSelectedReward(reward)
    setIsRedeemModalOpen(true)
  }

  const handleConfirmRedeem = async () => {
    if (!selectedReward || !userProfile) return

    const canAfford = selectedReward.currency === 'points' 
      ? userProfile.victory_points >= selectedReward.cost
      : (userProfile.field_passes || 0) >= selectedReward.cost

    if (!canAfford) {
      toast({
        title: 'Insufficient Balance',
        description: `Not enough ${selectedReward.currency === 'points' ? 'points' : 'Field Passes'}. ${selectedReward.currency === 'points' ? 'Earn more through matches or passes.' : 'Purchase more Field Passes to continue.'}`,
        variant: 'destructive'
      })
      setIsRedeemModalOpen(false)
      return
    }

    // Simulate redemption
    toast({
      title: 'Reward Redeemed!',
      description: `${selectedReward.title} has been added to your inventory.`,
    })

    setIsRedeemModalOpen(false)
    setSelectedReward(null)
  }

  const canAfford = (reward: RewardItem) => {
    if (!userProfile) return false
    return reward.currency === 'points' 
      ? userProfile.victory_points >= reward.cost
      : (userProfile.field_passes || 0) >= reward.cost
  }

  const featuredRewards = SAMPLE_REWARDS.filter(r => r.featured)
  const regularRewards = SAMPLE_REWARDS.filter(r => !r.featured)

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        
        <SidebarInset className="flex-1">
          <AppHeader 
            userProfile={userProfile ? {
              username: userProfile.username,
              avatar_url: userProfile.avatar_url,
              victory_points: userProfile.victory_points,
              field_passes: userProfile.field_passes || 0
            } : undefined}
          />
          
          <main className="flex-1 p-6">
            {/* Top Section */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-2">Rewards Shop</h1>
              <p className="text-muted-foreground mb-6">
                Use your points and Field Passes to unlock exclusive items.
              </p>
              
              {/* Current Balances */}
              <div className="flex gap-6">
                <Card className="bg-gradient-primary">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Trophy className="h-8 w-8 text-primary-foreground" />
                      <div>
                        <p className="text-sm text-primary-foreground/80">Rewards Points</p>
                        <p className="text-2xl font-mono font-bold text-primary-foreground">
                          {userProfile?.victory_points?.toLocaleString() || '0'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-accent">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Ticket className="h-8 w-8 text-accent-foreground" />
                      <div>
                        <p className="text-sm text-accent-foreground/80">Field Passes</p>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl font-mono font-bold text-accent-foreground">
                            {userProfile?.field_passes?.toLocaleString() || '0'}
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-6 w-6 p-0 border-accent-foreground/20 hover:bg-accent-foreground/10"
                            onClick={() => navigate('/buy-field-pass')}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* Main Shop Content */}
              <div className="col-span-12 lg:col-span-8">
                {/* Featured Rewards */}
                {featuredRewards.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                      <Star className="h-6 w-6 text-yellow-500" />
                      Featured Rewards
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {featuredRewards.map((reward) => (
                        <Card 
                          key={reward.id} 
                          className="relative overflow-hidden hover:shadow-premium hover:scale-105 transition-all duration-300 border-primary/20 bg-gradient-to-br from-card to-card/80"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent" />
                          <CardHeader className="relative">
                            <div className="flex items-start justify-between">
                              <div className="text-4xl mb-2">{reward.image}</div>
                              <Badge className={`${getRarityColor(reward.rarity)} border`}>
                                {reward.rarity}
                              </Badge>
                            </div>
                            <CardTitle className="text-lg">{reward.title}</CardTitle>
                            <CardDescription>{reward.description}</CardDescription>
                          </CardHeader>
                          <CardContent className="relative">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {reward.currency === 'points' ? (
                                  <Trophy className="h-4 w-4 text-primary" />
                                ) : (
                                  <Ticket className="h-4 w-4 text-accent" />
                                )}
                                <span className="font-mono font-bold text-lg">
                                  {reward.cost.toLocaleString()}
                                </span>
                              </div>
                              <Button
                                variant={canAfford(reward) ? "join" : "secondary"}
                                disabled={!canAfford(reward)}
                                onClick={() => handleRedeemClick(reward)}
                                className="font-semibold"
                              >
                                {canAfford(reward) ? 'Redeem Now' : 'Not Enough'}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Regular Rewards Grid */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <ShoppingBag className="h-6 w-6" />
                    All Rewards
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {regularRewards.map((reward) => (
                      <Card 
                        key={reward.id}
                        className="hover:shadow-lg hover:scale-105 transition-all duration-200"
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="text-3xl mb-2">{reward.image}</div>
                            <Badge className={getRarityColor(reward.rarity)}>
                              {reward.rarity}
                            </Badge>
                          </div>
                          <CardTitle className="text-base">{reward.title}</CardTitle>
                          <CardDescription className="text-sm">{reward.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {reward.currency === 'points' ? (
                                <Trophy className="h-4 w-4 text-primary" />
                              ) : (
                                <Ticket className="h-4 w-4 text-accent" />
                              )}
                              <span className="font-mono font-semibold">
                                {reward.cost.toLocaleString()}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant={canAfford(reward) ? "join" : "secondary"}
                              disabled={!canAfford(reward)}
                              onClick={() => handleRedeemClick(reward)}
                            >
                              {canAfford(reward) ? 'Redeem' : 'Locked'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {/* Coming Soon Card */}
                    <Card className="border-dashed border-2 border-muted-foreground/20">
                      <CardContent className="p-6 text-center">
                        <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                        <h3 className="font-semibold text-muted-foreground mb-2">Coming Soon</h3>
                        <p className="text-sm text-muted-foreground">
                          Jerseys, Merch, and More
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <div className="col-span-12 lg:col-span-4 space-y-6">
                {/* My Inventory */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      My Inventory
                    </CardTitle>
                    <CardDescription>Your recently redeemed rewards</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {inventory.length > 0 ? (
                      <div className="space-y-3">
                        {inventory.slice(0, 3).map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-2 rounded border bg-muted/50">
                            <div>
                              <p className="font-medium text-sm">{item.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(item.redeemed_at).toLocaleDateString()}
                              </p>
                            </div>
                            {getStatusIcon(item.status)}
                          </div>
                        ))}
                        <Separator />
                        <Button variant="outline" size="sm" className="w-full">
                          View Full Inventory
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No items yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Earn More Points */}
                <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-primary" />
                      Earn More Points
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span>Win a match</span>
                        <span className="font-mono text-primary">+100</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Complete daily challenge</span>
                        <span className="font-mono text-primary">+50</span>
                      </div>
                      <div className="flex justify-between">
                        <span>League championship</span>
                        <span className="font-mono text-primary">+500</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full">
                      View All Challenges
                    </Button>
                  </CardContent>
                </Card>

                {/* Need Field Passes? */}
                <Card className="bg-gradient-to-br from-accent/5 to-transparent border-accent/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Ticket className="h-5 w-5 text-accent" />
                      Need Field Passes?
                    </CardTitle>
                    <CardDescription>
                      Unlock premium rewards and skip the grind
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="premium" 
                      className="w-full"
                      onClick={() => navigate('/buy-field-pass')}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Buy Field Passes
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </main>
        </SidebarInset>
      </div>

      {/* Redeem Modal */}
      <Dialog open={isRedeemModalOpen} onOpenChange={setIsRedeemModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-2xl">{selectedReward?.image}</span>
              Redeem Reward
            </DialogTitle>
            <DialogDescription>
              Confirm your reward redemption
            </DialogDescription>
          </DialogHeader>
          
          {selectedReward && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">{selectedReward.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{selectedReward.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Cost:</span>
                    {selectedReward.currency === 'points' ? (
                      <Trophy className="h-4 w-4 text-primary" />
                    ) : (
                      <Ticket className="h-4 w-4 text-accent" />
                    )}
                    <span className="font-mono font-bold">
                      {selectedReward.cost.toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-muted/50 p-3 rounded">
                <p className="text-sm">
                  <span className="font-medium">Current Balance:</span>
                  <span className="font-mono ml-2">
                    {selectedReward.currency === 'points' 
                      ? userProfile?.victory_points?.toLocaleString() || '0'
                      : (userProfile?.field_passes || 0).toLocaleString()
                    }
                  </span>
                </p>
                <p className="text-sm mt-1">
                  <span className="font-medium">After Redemption:</span>
                  <span className="font-mono ml-2">
                    {selectedReward.currency === 'points' 
                      ? ((userProfile?.victory_points || 0) - selectedReward.cost).toLocaleString()
                      : ((userProfile?.field_passes || 0) - selectedReward.cost).toLocaleString()
                    }
                  </span>
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRedeemModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmRedeem}>
              Confirm Redemption
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  )
}

export default Rewards